import {useCallback, useEffect, useMemo, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {useTypedSelector} from 'helpers/reducers'
import {useAppDispatch} from 'helpers/store'
import {OffersAction} from 'store/offers/OffersAction'
import {buildTableRequest} from 'utils/table/query'
import {applyTablePage} from 'utils/table/state'
import {useTable} from 'utils/hooks/useTable'
import {toast} from 'utils/toastUtils'
import {getPackagesCartPersonsCount} from 'utils/utility'
import {useOfferStatusOptions} from 'utils/offers/offersUtils'
import {useCompanyBranchTypeOptions} from 'utils/companyUtils'
import {Offer} from 'types/entities/Offer'
import {OfferListRow, OfferRowGroup} from 'components/leads/EditLeadModalComponents/LeadOffersListTypes'
import {
    buildOfferRowGroups,
    createOfferRowMatcher,
    getComparableValue,
} from 'utils/leads/leadOffersUtils'
import {useLeadOffersListColumns} from 'utils/hooks/useLeadOffersListColumns'
import {useLeadOffersActions} from 'utils/hooks/useLeadOffersActions'

export const useLeadOffersList = (leadId: number, offers?: Offer[]) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const lastParamsRef = useRef<string>('')
    const localTableStateSignatureRef = useRef<string>('')
    const { options: statusOptions } = useOfferStatusOptions()
    const { options: accountTypeOptions } = useCompanyBranchTypeOptions()
    const emptyDefaultRows = useMemo<OfferListRow[]>(() => [], [])
    const offersStateById = useTypedSelector((state) => {
        const byId = new Map<number, Offer>()
        state.offersState.all.forEach((offer) => {
            if (offer.id) {
                byId.set(offer.id, offer)
            }
        })
        return byId
    })

    const resolvedOffers = useMemo(() => {
        if (!offers?.length) {
            return offers ?? []
        }

        return offers.map((leadOffer) => {
            const refreshedOffer = offersStateById.get(leadOffer.id)
            if (!refreshedOffer) {
                return leadOffer
            }

            return {
                ...leadOffer,
                ...refreshedOffer,
                calculations: refreshedOffer.calculations ?? leadOffer.calculations,
                packagesCart: refreshedOffer.packagesCart ?? leadOffer.packagesCart,
            }
        })
    }, [offers, offersStateById])

    const { openOfferDetail, deleteOfferHandler } = useLeadOffersActions(leadId)

    const totalPersons = useMemo(
        () => resolvedOffers.reduce((acc, offer) => acc + getPackagesCartPersonsCount(offer.packagesCart), 0),
        [resolvedOffers]
    )

    const totalPrice = useMemo(
        () => resolvedOffers.reduce((acc, offer) => acc + (offer.packagesCart?.price ?? 0), 0),
        [resolvedOffers]
    )

    const rowGroups = useMemo<OfferRowGroup[]>(
        () => buildOfferRowGroups(resolvedOffers, t),
        [resolvedOffers, t]
    )

    const columns = useLeadOffersListColumns({ onEdit: openOfferDetail, onDelete: deleteOfferHandler })

    const tableInit = useTable({
        tableId: 'lead-offers-list-table',
        columns,
        defaultSorting: [{ id: 'id', desc: true }],
        defaultRecordsPerPage: 50,
        defaultData: emptyDefaultRows,
    })

    const matchesRow = useCallback(
        createOfferRowMatcher(tableInit.columnFilters, tableInit.globalFilter, t),
        [tableInit.columnFilters, tableInit.globalFilter, t]
    )

    useEffect(() => {
        if (offers !== undefined) {
            const filteredGroups = rowGroups
                .map((group) => {
                    const parentMatches = matchesRow(group.parent)
                    const matchingChildren = group.children.filter(matchesRow)
                    if (parentMatches) return group
                    if (matchingChildren.length > 0) return { ...group, children: matchingChildren }
                    return null
                })
                .filter((group): group is OfferRowGroup => group !== null)

            const activeSort = tableInit.sorting[0]
            const sortedGroups = [...filteredGroups].sort((groupA, groupB) => {
                if (!activeSort) return groupB.parent.offer.id - groupA.parent.offer.id
                const valueA = getComparableValue(groupA.parent, activeSort.id)
                const valueB = getComparableValue(groupB.parent, activeSort.id)
                if (valueA === valueB) return groupB.parent.offer.id - groupA.parent.offer.id
                const direction = activeSort.desc ? -1 : 1
                return valueA > valueB ? direction : -direction
            })

            const totalGroups = sortedGroups.length
            const maxPageIndex = totalGroups > 0 ? Math.max(0, Math.ceil(totalGroups / tableInit.pageSize) - 1) : 0
            const nextPageIndex = Math.min(tableInit.pageIndex, maxPageIndex)
            const startIndex = nextPageIndex * tableInit.pageSize
            const paginatedGroups = sortedGroups.slice(startIndex, startIndex + tableInit.pageSize)
            const paginatedRows = paginatedGroups.flatMap((group) => [group.parent, ...group.children])
            const rowIdsSignature = paginatedRows.map((row) => row.id).join('|')
            const nextSignature = `${nextPageIndex}:${tableInit.pageSize}:${totalGroups}:${rowIdsSignature}`

            if (localTableStateSignatureRef.current !== nextSignature) {
                localTableStateSignatureRef.current = nextSignature
                applyTablePage(tableInit, {
                    data: paginatedRows,
                    current_page: nextPageIndex + 1,
                    per_page: tableInit.pageSize,
                    total: totalGroups,
                })
            } else if (tableInit.isLoading) {
                tableInit.setIsLoading(false)
            }
            return
        }

        const params = buildTableRequest({
            tableInit,
            transformSearch: (filters) => [...filters, { id: 'lead_id', value: leadId }],
        })

        if (tableInit.isLoading) return

        const currentParams = JSON.stringify({
            leadId,
            pageIndex: tableInit.pageIndex,
            pageSize: tableInit.pageSize,
            globalFilter: tableInit.globalFilter,
            sorting: tableInit.sorting,
            columnFilters: tableInit.columnFilters,
        })

        if (lastParamsRef.current === currentParams) return
        lastParamsRef.current = currentParams

        tableInit.setIsLoading(true)
        dispatch(OffersAction.fetchOffersTablePage(params))
            .then((res) => {
                const flatRows = buildOfferRowGroups(res.data ?? [], t).flatMap((group) => [
                    group.parent,
                    ...group.children,
                ])
                applyTablePage(tableInit, {
                    ...res,
                    data: flatRows,
                })
            })
            .catch(() => toast.error(t('Failed to load offers')))
            .finally(() => tableInit.setIsLoading(false))
    }, [dispatch, leadId, matchesRow, offers, resolvedOffers, rowGroups, tableInit, t])

    const resetPage = useCallback(() => {
        tableInit.setPagination({ pageIndex: 0, pageSize: tableInit.pageSize })
    }, [tableInit])

    return {
        tableInit,
        statusOptions,
        accountTypeOptions,
        totalPersons,
        totalPrice,
        openOfferDetail,
        resetPage,
    }
}
